import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowLeft, Mail, RefreshCw } from "lucide-react";

export function TwoFactorView({ otp, setOtp, otpMessage, emailFailed, loading, resendCooldown, resending, onVerify, onResend, onBack, bgImage }) {
  return (
    <div className="flex min-h-screen">
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 uppercase mb-2" style={{ fontFamily: "Chivo, sans-serif", letterSpacing: "-0.02em" }}>DOZERWORLD</h1>
            <p className="text-slate-500">Inventory Management System</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-login-from-2fa-btn">
              <ArrowLeft size={16} /> Back to Login
            </button>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="text-orange-500" size={28} />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>2FA Verification</h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-6 bg-slate-50 p-3 rounded-sm border border-slate-200">
              <Mail size={16} className="text-orange-500 flex-shrink-0" />
              <span data-testid="otp-message">{otpMessage}</span>
            </div>
            {emailFailed && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="email-failed-warning">
                <p className="text-sm font-semibold text-amber-800">Email delivery failed</p>
                <p className="text-xs text-amber-700 mt-1">Please tap <strong>"Resend OTP"</strong> below to retry. If the issue persists, contact your administrator.</p>
              </div>
            )}
            <form onSubmit={onVerify} className="space-y-5">
              <div>
                <Label className="text-xs text-slate-600 font-bold uppercase">Enter OTP</Label>
                <Input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required maxLength={6} placeholder="Enter 6-digit OTP" className="mt-1 rounded-sm border-slate-300 text-center text-2xl tracking-[0.5em] font-mono" autoFocus data-testid="otp-input" />
              </div>
              <Button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-orange-600 text-white hover:bg-orange-700 rounded-sm uppercase font-bold" data-testid="verify-otp-btn">
                {loading ? "Verifying..." : "Verify OTP"}
              </Button>
            </form>
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-slate-500">{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't receive OTP?"}</p>
              <Button variant="ghost" size="sm" onClick={onResend} disabled={resendCooldown > 0 || resending} className="text-orange-600 hover:text-orange-700 text-xs" data-testid="resend-otp-btn">
                <RefreshCw size={14} className={`mr-1 ${resending ? "animate-spin" : ""}`} />
                {resending ? "Sending..." : "Resend OTP"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-cover bg-center relative" style={{ backgroundImage: bgImage }}>
        <div className="absolute inset-0 bg-slate-900/40"></div>
      </div>
    </div>
  );
}

export function PasswordResetView({ resetStep, resetEmail, setResetEmail, resetOtp, setResetOtp, newPassword, setNewPassword, confirmPassword, setConfirmPassword, loading, onRequestOTP, onResetPassword, onBack, bgImage }) {
  return (
    <div className="flex min-h-screen">
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 uppercase mb-2" style={{ fontFamily: "Chivo, sans-serif", letterSpacing: "-0.02em" }}>DOZERWORLD</h1>
            <p className="text-slate-500">Inventory Management System</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-login-from-reset-btn">
              <ArrowLeft size={16} /> Back to Login
            </button>
            <h2 className="text-xl font-bold text-slate-900 uppercase mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>Reset Password</h2>
            {resetStep === 1 ? (
              <form onSubmit={onRequestOTP} className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600 font-bold uppercase">Email Address</Label>
                  <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required placeholder="Enter your email" className="mt-1 rounded-sm border-slate-300" data-testid="reset-email-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-600 text-white hover:bg-orange-700 rounded-sm" data-testid="send-reset-otp-btn">
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
              </form>
            ) : (
              <form onSubmit={onResetPassword} className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600 font-bold uppercase">OTP</Label>
                  <Input type="text" value={resetOtp} onChange={(e) => setResetOtp(e.target.value)} required maxLength={6} placeholder="Enter 6-digit OTP" className="mt-1 rounded-sm border-slate-300 text-center text-lg tracking-widest" data-testid="reset-otp-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-bold uppercase">New Password</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} placeholder="Min 8 characters" className="mt-1 rounded-sm border-slate-300" data-testid="new-password-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-bold uppercase">Confirm Password</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="Confirm new password" className="mt-1 rounded-sm border-slate-300" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-600 text-white hover:bg-orange-700 rounded-sm" data-testid="reset-password-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-cover bg-center relative" style={{ backgroundImage: bgImage }}>
        <div className="absolute inset-0 bg-slate-900/40"></div>
      </div>
    </div>
  );
}
